import type { Fetcher } from "../fetcher";
import type { ApiCollectionResponse, ApiSingleResponse } from "../types/api";
import type {
  CreatePaymentAllocationDto,
  CreatePaymentDto,
  ListPaymentsParams,
  PaymentDTO,
  PaymentListStats,
  UpdatePaymentDto,
} from "./types";

const EMPTY_STATS: PaymentListStats = {
  receivedTotal: 0,
  receivedCount: 0,
  unallocatedTotal: 0,
};

export function createPaymentsRepository(fetcher: Fetcher) {
  return {
    async list(params: ListPaymentsParams = {}): Promise<{
      items: PaymentDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
      stats: PaymentListStats;
    }> {
      const qs = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) qs.append(key, String(value));
      }
      const query = qs.toString();
      const res = await fetcher<
        ApiCollectionResponse<PaymentDTO, { stats?: PaymentListStats }>
      >(`/payments${query ? `?${query}` : ""}`);
      const payload = res.data ?? ({} as Partial<{
        items: PaymentDTO[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
        stats: PaymentListStats;
      }>);
      const pagination = (payload.pagination ?? {}) as Partial<{
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      }>;
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 20,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 1,
        },
        stats: payload.stats ?? EMPTY_STATS,
      };
    },

    async get(id: string): Promise<PaymentDTO> {
      const res = await fetcher<ApiSingleResponse<PaymentDTO>>(
        `/payments/${id}`,
      );
      return res.data;
    },

    /** Records the money and applies it, in one request. */
    async create(payload: CreatePaymentDto): Promise<PaymentDTO> {
      const res = await fetcher<ApiSingleResponse<PaymentDTO>>("/payments", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return res.data;
    },

    async update(id: string, payload: UpdatePaymentDto): Promise<PaymentDTO> {
      const res = await fetcher<ApiSingleResponse<PaymentDTO>>(
        `/payments/${id}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return res.data;
    },

    /** Reverses the payment and every allocation it holds. */
    async voidPayment(id: string, reason: string): Promise<PaymentDTO> {
      const res = await fetcher<ApiSingleResponse<PaymentDTO>>(
        `/payments/${id}/void`,
        { method: "POST", body: JSON.stringify({ reason }) },
      );
      return res.data;
    },

    async allocate(
      id: string,
      payload: CreatePaymentAllocationDto,
    ): Promise<PaymentDTO> {
      const res = await fetcher<ApiSingleResponse<PaymentDTO>>(
        `/payments/${id}/allocations`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      return res.data;
    },

    /** Un-applies money from one invoice; the payment itself stays posted. */
    async voidAllocation(
      id: string,
      allocationId: string,
      reason?: string,
    ): Promise<PaymentDTO> {
      const res = await fetcher<ApiSingleResponse<PaymentDTO>>(
        `/payments/${id}/allocations/${allocationId}/void`,
        {
          method: "POST",
          body: JSON.stringify(reason ? { reason } : {}),
        },
      );
      return res.data;
    },

    // No delete: a posted payment is reversed, never removed.
  };
}
